import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { restorePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { AuthorizationStatus } from 'src/common/enumerations/authorization-status';
import { CardType } from 'src/common/enumerations/card-type';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { PaymentException } from 'src/common/exceptions/paymentException';
import { DataUndefined } from 'src/common/helpers/error-code';
import { IItemInputData } from 'src/common/interfaces/IItemInputData';
import { store } from 'src/common/redux/configure-store';
import { BalanceInquiryActions } from 'src/common/redux/reducers/balance-inquiry-reducer';
import { Status } from 'src/devices/payment/dtos/eps-data';
import { getCardData, resetCardData } from 'src/devices/payment/get-card-details';
import {
  activateOnlineItem,
  authorizeOnlineItemDto,
  beginPaymentSession,
  cancelPinpadOperation,
  endPaymentSession,
  inPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { cancelUnPaidPreTender } from 'src/home-page/business-logic/cancel-tender-payment';
import { makeTenderPayment } from 'src/home-page/business-logic/make-tender-payment';
import { dataAdd } from 'src/home-page/business-logic/service/data-add';
import { IItemLookupCriteria } from 'src/items-list/business-logic/service/product-lookup';
import { productLookupByPolicy } from 'src/items-list/business-logic/service/product-lookup-by-policy';
import { popupAsyncMessage, popupMessage } from 'src/messages/business-logic/popup-message';
import { lookupOnlineItemBehavior } from 'src/online-items/business-logic/get-online-item-behavior';
import { ActivationData } from 'src/online-items/dto/CartItemActivation';
import { OnlineItemExternalId } from 'src/online-items/enumerations/OnlineItemAuthorizationTrigger';
import {
  IGiftCardRefundProps,
  showGiftCardRefundContainer,
} from '../components/gift-card-refund-container';

export const handleGiftCardRefund = async (): Promise<void> => {
  await cancelPinpadOperation();

  return new Promise<void>((resolve, reject) => {
    const onSwipe = async (): Promise<void> => {
      return getSwipedGiftCardExternalId()
        .then(externalId => activateGiftCard(true, externalId))
        .then(makeRefundPayment)
        .then(resolve)
        .catch(handleRefundError)
        .catch(reject);
    };

    const onSubmitData = async (itemId: string, entryMethod: EntryMethod): Promise<void> => {
      return withSpinner(() =>
        getCodedCardExternalId(itemId, entryMethod)
          .then(externalId => activateGiftCard(false, externalId))
          .then(makeRefundPayment)
          .then(resolve)
          .catch(err => handleRefundError(err, { itemId, entryMethod }))
          .catch(reject),
      );
    };

    const onCancel = () => reject(new AbortedError());

    const onScan = (data: string) => onSubmitData(data, EntryMethod.Scanned);
    const onKeypadSubmit = (data: string) => onSubmitData(data, EntryMethod.Keyed);

    const props: IGiftCardRefundProps = {
      onCancel,
      onKeypadSubmit,
      onSwipe,
    };

    showGiftCardRefundContainer(props, onScan);
  }).finally(() => {
    store.dispatch(BalanceInquiryActions.SetAuthorizationExternalIdType());
    restorePage();
    cleanPinpad();
  });
};

const getSwipedGiftCardExternalId = async (): Promise<string | undefined> => {
  const epsData = getCardData();
  if (epsData?.card.cardType?.toUpperCase() !== CardType.GiftCard) {
    throw new EmeraldErrorModal({
      type: EmeraldDialogTypes.Info,
      message: 'InvalidGiftCardTitle',
      subMessage: 'InvalidGiftCardSubtitle',
    });
  }
  return epsData.card.pan;
};

const getCodedCardExternalId = async (data: string, entryMethod: EntryMethod): Promise<string> => {
  const authorizationExternalIdType = store.getState().balanceInquiry.authorizationExternalIdType;

  if (authorizationExternalIdType === OnlineItemExternalId.Card) {
    throw new EmeraldErrorModal({
      message: 'GiftCardPleaseSwipeCard',
      subMessage: 'GiftCardInsertSwipeOrTap',
      type: EmeraldDialogTypes.Info,
    });
  }

  const inputData: IItemInputData = { itemId: data, entryMethod };
  const transactionId = store.getState().transaction.transactionId;
  return dataAdd(transactionId, inputData).then(response =>
    response.onlineItemExternalId ? response.onlineItemExternalId : data,
  );
};

const activateGiftCard = async (
  isIdFromSwipe: boolean,
  externalId?: string,
): Promise<ActivationData | undefined> => {
  if (externalId) {
    if (!isIdFromSwipe) {
      await cleanPinpad();
    }
    if (!inPaymentSession()) {
      await beginPaymentSession();
    }

    const authorizeOnlineItemData: authorizeOnlineItemDto = {
      externalId,
      price: Math.abs(store.getState().itemList.amountDue).toString(),
    };

    const activationResponse = await activateOnlineItem(authorizeOnlineItemData);

    const activationData: ActivationData = {
      authorizationCode: activationResponse.paymentDetails?.authCode,
      referenceNumber: activationResponse.paymentDetails?.paymentTranID,
      maskedAccountNumber: externalId,
    };

    if (activationResponse.status === Status.Success) {
      activationData.authorizationStatus = AuthorizationStatus.Success;
    }

    return activationData;
  }
};

const makeRefundPayment = async (activationData?: ActivationData) => {
  if (activationData) {
    const unpaidPreTender = store.getState().tenderMenu.unPaidPreTender;
    if (unpaidPreTender) {
      unpaidPreTender.epsData = {
        authorization: {
          authorizationCode: activationData.authorizationCode,
          referenceNumber: activationData.referenceNumber,
          authorizationMethod: 'Online',
        },
        card: { maskedAccountNumber: activationData.maskedAccountNumber },
      };
      await makeTenderPayment(unpaidPreTender);
    }
  }
};

const handleRefundError = async (err: EmeraldError, inputData?: IItemInputData): Promise<void> => {
  if (err.code === DataUndefined) {
    handleGetIdError(inputData);
  } else if (err instanceof PaymentException) {
    await popupAsyncMessage(
      new EmeraldErrorModal({
        type: EmeraldDialogTypes.Info,
        message: 'GiftCardActivationFailed',
        subMessage: 'GiftCardActivationFailedSubmessage',
      }),
    );
  } else if (err instanceof EmeraldErrorModal) {
    await popupAsyncMessage(err);
  } else {
    await clearPretender();
    resetCardData();
    throw new EmeraldErrorModal({
      type: EmeraldDialogTypes.Info,
      message: 'PosStartErrorSubMessage',
    });
  }

  resetCardData();
};

const handleGetIdError = async (inputData?: IItemInputData) => {
  const itemLookUp: IItemLookupCriteria = {
    scanData: [{ data: inputData?.itemId }],
    entryMethod: inputData?.entryMethod,
  };
  return productLookupByPolicy(itemLookUp)
    .then(itemDetails => {
      const groupCategory = itemDetails.find(item => item.itemId === inputData?.itemId)
        ?.linkedItemsGroup?.groupCategory;
      return lookupOnlineItemBehavior(groupCategory).then(
        itemBehavior => itemBehavior.authorizationExternalIdType,
      );
    })
    .then(authType => {
      if (
        authType === OnlineItemExternalId.Card ||
        authType === OnlineItemExternalId.SecondBarcode
      ) {
        store.dispatch(BalanceInquiryActions.SetAuthorizationExternalIdType(authType));
      }
    })
    .catch(popupMessage);
};

const clearPretender = async (): Promise<void> => {
  const { unPaidPreTender } = store.getState().tenderMenu;
  if (unPaidPreTender) {
    return withSpinner(() =>
      cancelUnPaidPreTender(true)
        .finally(cleanPinpad)
        .catch(() => {}),
    );
  }
};

const cleanPinpad = async (): Promise<void> => {
  if (inPaymentSession()) {
    await cancelPinpadOperation();
    await endPaymentSession();
  }
};
